import ModalComponent from '@/components/antd-comp/modal/ModalComponent'
import { roleOrganization } from '@/controllers/API/assistant'
import type { TreeProps } from 'antd'
import { Empty, Spin, Tree } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import '../../pages/FileLibPage/css/index.css'
import InputAntDef from '../input/InputAntDef'
import './index.less'
/**
 * 权限分配对话框组件
 *
 * 这是一个功能完整的权限分配对话框，支持组织架构树形展示、搜索、多选等功能。
 * 主要用于分配用户权限、部门权限等场景，提供直观的树形结构选择界面。
 *
 * @param cancel - 取消回调函数，关闭对话框时调用
 * @param submit - 确认回调函数，提交选中的权限数据时调用，参数为选中的项目数组
 * @param title - 对话框标题，默认为'授权管理'
 * @param optionsValues - 初始选中的项目数组，格式为[{id: string, name: string}]
 * @param filter_department - 是否过滤部门，默认为false
 * @param departmentType - 部门类型，0=选人模式，4=选部门模式
 *
 * 使用场景：
 * 1. 用户权限分配 - 为特定用户分配系统权限
 * 2. 角色权限管理 - 配置角色的权限范围
 * 3. 部门权限设置 - 设置部门级别的权限
 * 4. 项目成员管理 - 添加项目成员并分配权限
 * 5. 工作流审批配置 - 设置审批流程中的审批人
 * 6. 数据访问权限 - 配置数据的访问权限
 * 7. 功能模块权限 - 分配功能模块的使用权限
 * 8. 组织架构管理 - 管理组织架构中的权限关系
 *
 * 使用示例：
 * ```tsx
 * // 基础用法
 * const [accredit, setAccredit] = useState(false)
 *
 * {accredit && (
 *   <AccreditDia
 *     cancel={() => setAccredit(false)}
 *     submit={(checkedValues) => {
 *       console.log('选中的权限:', checkedValues)
 *       setAccredit(false)
 *     }}
 *   />
 * )}
 *
 * // 带初始选中项
 * <AccreditDia
 *   title="分配项目权限"
 *   optionsValues={[
 *     { id: 'user1', name: '张三' },
 *     { id: 'dept1', name: '技术部' }
 *   ]}
 *   departmentType={0} // 选人模式
 *   cancel={() => setAccredit(false)}
 *   submit={(checkedValues) => {
 *     // 处理选中的权限数据
 *     handlePermissionSubmit(checkedValues)
 *     setAccredit(false)
 *   }}
 * />
 *
 * // 部门选择模式
 * <AccreditDia
 *   title="选择部门"
 *   departmentType={4} // 选部门模式
 *   filter_department={true}
 *   cancel={() => setAccredit(false)}
 *   submit={(checkedValues) => {
 *     // 处理选中的部门
 *     handleDepartmentSubmit(checkedValues)
 *     setAccredit(false)
 *   }}
 * />
 * ```
 */
const AccreditDia = ({
  cancel,
  submit,
  title = '授权管理',
  optionsValues = [],
  filter_department = false,
  departmentType = 0
}) => {
  const { t } = useTranslation()

  // ==================== 状态管理 ====================
  /* 选中数据管理 */
  const [checkedValues, setcheckedValues] = useState([]) as any // 当前选中的项目ID数组
  const [keyToTitleMap, setKeyToTitleMap] = useState({}) // key到title的映射表，用于显示选中项的名称

  /* 加载状态 */
  const [Loading, setLoading] = useState(false) // 数据加载状态

  /* 搜索功能 */
  const [SearchName, setSearchName] = useState('') // 搜索关键词

  /* 树形数据 */
  const [options, setoptions] = useState([]) // 组织架构树形数据
  /**
   * 获取组织架构数据
   * @param name - 搜索关键词，为空时获取全部数据
   */
  let getInfo = (name = '') => {
    setoptions([])
    roleOrganization(name, filter_department)
      .then(res => {
        const responseData = res.data || res // 兼容不同的响应格式
        setoptions(generateTree(responseData))

        // 如果有搜索关键词，根据搜索结果自动展开
        if (name) {
          const expandedKeys = getExpandedKeysFromSearch(responseData)
          setExpandedKeys(expandedKeys)
          setAutoExpandParent(true)
        } else {
          // 清空搜索时，重置展开状态
          setExpandedKeys([])
          setAutoExpandParent(false)
        }

        setLoading(false)
      })
      .catch(err => {
        console.log(err)
        setLoading(false)
      })
  }

  /**
   * 根据后端返回的 open 字段计算需要展开的节点
   * 用于搜索时自动展开包含搜索结果的父节点
   * @param data - 组织架构数据
   * @returns 需要展开的节点key数组
   */
  const getExpandedKeysFromSearch = (data: any[]): React.Key[] => {
    const expandedKeys: React.Key[] = []

    const traverse = (items: any[]): React.Key[] => {
      const localExpandedKeys: React.Key[] = []

      items.forEach(item => {
        // 根据字段参数控制展开：如果部门有 open 字段且为 true，则展开
        if (item.open === true) {
          localExpandedKeys.push(item.depart_id)
        }

        // 递归处理子部门
        if (Array.isArray(item.nodes_depart) && item.nodes_depart.length > 0) {
          // 先递归处理子部门，看看是否有匹配结果
          const childExpandedKeys = traverse(item.nodes_depart)

          // 如果子部门有匹配结果，也要展开父部门
          if (childExpandedKeys.length > 0) {
            localExpandedKeys.push(item.depart_id)
          }

          // 将子部门的展开keys也加入到当前层级的结果中
          localExpandedKeys.push(...childExpandedKeys)
        }
      })

      return localExpandedKeys
    }

    // 直接处理整个数据数组
    const result = traverse(data)
    return Array.from(new Set(result)) // 去重
  }

  /**
   * 递归生成树结构
   * 将后端返回的扁平化组织架构数据转换为树形结构
   * @param data - 后端返回的组织架构数据
   * @returns 树形结构数据
   */
  const generateTree = data => {
    const map = {} // 临时映射表

    const traverse = items => {
      return items.map(item => {
        const node = {
          title: item.depart_name || '暂无数据', // 默认标题为 '暂无数据'
          key: item.depart_id,
          children: [],
          disabled: false // 默认不禁用
        }
        map[item.depart_id] = item.depart_name || '暂无数据' // 添加到映射表

        // 处理部门下的成员列表
        const hasMembers = Array.isArray(item.user_list) && item.user_list.length > 0
        if (hasMembers) {
          node.children = node.children.concat(
            item.user_list.map(user => {
              map[user.lenovo_id] = user.user_name // 添加到映射表
              return {
                title: user.user_name,
                key: user.lenovo_id,
                isLeaf: true,
                disabled: user.disabled // 成员是否禁用
              }
            })
          )
        }

        // 处理子部门
        const hasSubDepartments = Array.isArray(item.nodes_depart) && item.nodes_depart.length > 0
        if (hasSubDepartments) {
          node.children = node.children.concat(traverse(item.nodes_depart))
        }

        // 如果没有成员且没有子部门，则禁用该部门  departmentType = 4 = 选择部门
        if (!hasMembers && !hasSubDepartments && departmentType != 4) {
          node.disabled = true
          node.title = `${node.title}（暂无数据）`
        }

        return node
      })
    }

    // 处理 roleOrganization 接口返回的数据结构，跳过第一层公司
    if (data && Array.isArray(data) && data.length > 0 && 'depart_id' in data[0]) {
      const firstLevel = data[0]
      const result = []

      // 先处理第一层的部门（跳过公司层）
      if (Array.isArray(firstLevel.nodes_depart) && firstLevel.nodes_depart.length > 0) {
        // 直接处理子部门，不创建公司节点
        result.push(
          ...firstLevel.nodes_depart.map(item => {
            const node = {
              title: item.depart_name || '暂无数据',
              key: item.depart_id,
              children: [],
              disabled: false
            }
            map[item.depart_id] = item.depart_name || '暂无数据' // 添加到映射表

            // 先处理子部门
            if (Array.isArray(item.nodes_depart) && item.nodes_depart.length > 0) {
              node.children = node.children.concat(traverse(item.nodes_depart))
            }

            // 再处理部门下的成员列表
            if (Array.isArray(item.user_list) && item.user_list.length > 0) {
              node.children = node.children.concat(
                item.user_list.map(user => {
                  map[user.lenovo_id] = user.user_name // 添加到映射表
                  return {
                    title: user.user_name,
                    key: user.lenovo_id,
                    isLeaf: true,
                    disabled: user.disabled
                  }
                })
              )
            }

            // 如果该部门没有成员和子部门，标记为禁用
            if (!item.user_list?.length && !item.nodes_depart?.length && departmentType != 4) {
              node.disabled = true
              node.title = `${node.title}（暂无数据）`
            }

            return node
          })
        )
      }

      // 再处理第一层的用户（跳过公司层）
      if (Array.isArray(firstLevel.user_list) && firstLevel.user_list.length > 0) {
        result.push(
          ...firstLevel.user_list.map(user => {
            map[user.lenovo_id] = user.user_name // 添加到映射表
            return {
              title: user.user_name,
              key: user.lenovo_id,
              isLeaf: true,
              disabled: user.disabled
            }
          })
        )
      }

      setKeyToTitleMap(prev => ({ ...prev, ...map })) // 合并映射，保留已选项名称
      return result
    }

    // 处理其他数据结构（保留原有逻辑）
    const treeData = traverse(Array.isArray(data) ? data : [])
    // const treeData = traverse(data)
    setKeyToTitleMap(prev => ({ ...prev, ...map })) // 合并映射，保留已选项名称
    return treeData
  }

  useEffect(() => {
    const init = async () => {
      setLoading(true)
      await getInfo()
      // 等待数据加载完成
      if (optionsValues.length > 0) {
        const initialCheckedValues = optionsValues.map(item => item.id) // 提取 id 列表
        const initialKeyToTitleMap = optionsValues.reduce((map, item) => {
          map[item.id] = item.name // 构建 { id: name } 映射表
          return map
        }, {})
        setcheckedValues(initialCheckedValues) // 设置选中的 keys
        setKeyToTitleMap(prevMap => ({ ...prevMap, ...initialKeyToTitleMap })) // 合并到现有映射表
      } else {
        setcheckedValues([]) // 设置选中的 keys
        setKeyToTitleMap({})
      }
      setLoading(false)
    }

    init()
  }, [])

  /**
   * 删除已选项
   * 从已选列表中移除指定索引的项目
   * @param index - 要删除的项目索引
   * @param key - 要删除的项目key（未使用，保留兼容性）
   */
  const sliceCheckedValues = (index, key) => {
    const newCheckedValues = [...checkedValues]
    newCheckedValues.splice(index, 1)
    setcheckedValues(newCheckedValues)
  }

  // ==================== 树形控件状态 ====================
  /* 自动展开父节点 */
  const [autoExpandParent, setAutoExpandParent] = useState<boolean>(false)
  /* 展开的数据 */
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([])

  /**
   * 树形控件展开/收起事件处理
   * @param expandedKeysValue - 当前展开的节点key数组
   */
  const onExpand: TreeProps['onExpand'] = expandedKeysValue => {
    console.log('onExpand', expandedKeysValue)
    setExpandedKeys(expandedKeysValue)
    setAutoExpandParent(false)
  }

  /**
   * 收集当前树可见的所有 key（用于保留搜索前已选项）
   * 避免搜索后列表刷新导致已选项丢失
   * @param nodes - 树形数据节点
   * @returns 可见节点key的Set集合
   */
  const collectVisibleKeys = (nodes: any[]): Set<string> => {
    const keys: string[] = []
    const walk = (arr: any[]) => {
      arr?.forEach(n => {
        if (!n) return
        if (n.key) keys.push(n.key)
        if (Array.isArray(n.children) && n.children.length) walk(n.children)
      })
    }
    walk(nodes || [])
    return new Set(keys)
  }

  /**
   * 树形控件选中事件处理
   * 处理用户选择/取消选择节点时的逻辑
   * @param checkedKeysValue - 选中的节点key数组
   * @param e - 事件对象，包含checkedNodes等信息
   */
  const onCheck: TreeProps['onCheck'] = (checkedKeysValue: any, e: any) => {
    const { checkedNodes } = e

    // 判断是否全选
    let filteredKeys: string[]
    // 部门模式：使用标准的级联选择
    if (departmentType === 4) {
      // 在标准模式下，checkedKeysValue 包含所有选中的节点（包括父节点和子节点）
      // 我们只需要过滤出部门节点（非叶子节点）
      filteredKeys = checkedKeysValue.filter((key: string) => {
        // 检查这个 key 是否对应一个部门节点
        const isDepartment = (nodes: any[]): boolean => {
          for (const node of nodes) {
            if (node.key === key) {
              return !node.isLeaf // 部门节点不是叶子节点
            }
            if (node.children) {
              if (isDepartment(node.children)) {
                return true
              }
            }
          }
          return false
        }
        return isDepartment(options)
      })
    } else {
      // 原“选人模式”保持不变
      filteredKeys = checkedNodes.filter((n: any) => n.isLeaf).map((n: any) => n.key)
    }

    // 保留不可见项（当前搜索结果之外）的既有选择，避免搜索后列表刷新导致已选丢失
    const visibleSet = collectVisibleKeys(options)
    const preserved = checkedValues.filter((k: any) => !visibleSet.has(k))

    // 目标集合（去重）
    const unionKeys = [...preserved, ...filteredKeys]
    const unionSet = new Set(unionKeys)

    // 1) 先保留旧序中的仍勾选项，保持原有顺序不变
    const keptOldOrder = checkedValues.filter((k: any) => unionSet.has(k))
    // 2) 再把新增的（旧序中没有的）追加到末尾，作为新选择
    const additions = unionKeys.filter(k => !checkedValues.includes(k))

    setcheckedValues([...keptOldOrder, ...additions])
  }

  const onSelect: TreeProps['onSelect'] = (selectedKeysValue, info) => {
    console.log('onSelect', info)
    console.log('selectedKeysValue', selectedKeysValue)
    // setcheckedValues(selectedKeysValue)
  }

  /**
   * 渲染左侧选择区域
   * 包含搜索框和树形选择器
   * @returns 左侧区域的JSX
   */
  const caseLeft = () => {
    return (
      <div
        className="accreditDia_case flexCol"
        style={{ borderTopRightRadius: '0', borderBottomRightRadius: '0', paddingTop: '0' }}
      >
        {/* 搜索 */}
        <div className="flexRow flexRowJustBet accreditTitle" style={{ height: '48px' }}>
          <InputAntDef
            className="accreditDia_input h-[32px] w-[100%]"
            searchFun={e => {
              setSearchName(e.target.value)
              setLoading(true)
              getInfo(e.target.value)
            }}
            placeholder={'输入部门、人名搜索公司成员'}
          ></InputAntDef>
        </div>
        {/* ai推荐 自定义创建 */}
        <div className="caseOverFlowY_tagDialog">
          {options.length === 0 && <Empty description={t('empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />}
          <Spin spinning={Loading}>
            <Tree
              height={380}
              checkable
              /* 展开/收起节点时触发	 */
              onExpand={onExpand}
              /*  展开的data */
              expandedKeys={expandedKeys}
              /* 自动展开父节点 */
              autoExpandParent={autoExpandParent}
              /* 选中 */
              onCheck={onCheck}
              checkedKeys={checkedValues}
              /* 选择 */
              onSelect={onSelect}
              /* 受控 = 设置选中的树节点，多选需设置 multiple 为 true */
              selectedKeys={[]}
              treeData={options}
            />
          </Spin>
        </div>
      </div>
    )
  }
  /**
   * 渲染右侧已选区域
   * 显示已选择的项目和清空功能
   * @returns 右侧区域的JSX
   */
  const caseRight = () => {
    return (
      <div
        className="accreditDia_case flexCol"
        style={{ borderTopLeftRadius: '0', borderBottomLeftRadius: '0', borderLeft: '0', paddingTop: '0' }}
      >
        <div className="flexRow flexRowJustBet" style={{ height: '48px' }}>
          <span className="accreditTitle">
            已选{departmentType === 4 ? '部门' : '成员'}：
            <span className="accreditTitle_num">{checkedValues.length}</span>
          </span>
          <span
            className="accreditTitle_close cursonCase"
            onClick={() => {
              setcheckedValues([])
            }}
          >
            清空
          </span>
        </div>
        {checkedValues.length === 0 ? (
          <Empty description={t('empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} className="accreditDia_case_emtpy" />
        ) : (
          <div className="caseOverFlowY_tagDialog caseOverFlowY" style={{ overflowY: 'scroll' }}>
            {Array.isArray(checkedValues) &&
              checkedValues?.map((value, index) => {
                return (
                  <div key={value} className="flexRow flexRowJustBet caseMarBot">
                    <span>{keyToTitleMap[value]}</span>
                    <svg
                      onClick={e => {
                        sliceCheckedValues(index, value)
                      }}
                      className="cursonCase"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 4L12 12"
                        stroke="#7587A5"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4 12L12 4"
                        stroke="#7587A5"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )
              })}
          </div>
        )}
      </div>
    )
  }
  return (
    <ModalComponent
      title={title}
      width={640}
      content={
        <div className="flexCol">
          <div className="flexRowStart" style={{ marginTop: '12px', marginBottom: '8px' }}>
            {caseLeft()}
            {caseRight()}
          </div>
        </div>
      }
      okText={t('confirmButton')}
      cancelText={t('cancel')}
      onCancel={() => {
        cancel()
      }}
      onOk={() => {
        // showMessage({
        //   content: '授权成功',
        //   type: 'success'
        // })
        // 转换 checkedValues 为 [{ id: string, name: string }] 格式
        const formattedValues = checkedValues.map(key => ({
          id: key,
          name: keyToTitleMap[key]
        }))
        submit(formattedValues)
      }}
    />
  )
}

export default AccreditDia
